import {
  IconCopy,
  IconShare2,
  IconPlayerPlay,
  IconThumbUp,
  IconThumbDown,
  IconRefresh,
  IconClipboardCheck,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

interface ChatActionsProps {
  onCopy: () => void;
  onShare: () => void;
  // The share button is otherwise hidden (the play / thumbs actions stay
  // "coming soon"); surfaces that offer sharing flip this on.
  showShare: boolean;
  onPlay?: () => void;
  onThumbUp?: () => void;
  onThumbDown?: () => void;
  onRegenerate?: () => void;
  // Opens the expert-annotation sheet for this turn. Absent unless the turn is
  // one the rubric applies to — the caller decides, so this component stays
  // ignorant of modes.
  onEvaluate?: () => void;
  // Whether this turn already carries a verdict from this reader. Reopening
  // revises it, so the label has to say which of the two is about to happen.
  evaluated?: boolean;
}

export function ChatActions({
  onCopy,
  onShare,
  showShare,
  onPlay,
  onThumbUp,
  onThumbDown,
  onRegenerate,
  onEvaluate,
  evaluated,
}: ChatActionsProps): React.ReactElement {
  const { t } = useTranslation();
  const iconProps = { stroke: 1.5, className: "size-5.25 text-ds-black" };

  return (
    <div className="flex items-center gap-3">
      <button
        className="p-0 bg-transparent border-none cursor-pointer"
        onClick={onCopy}
      >
        <IconCopy {...iconProps} />
      </button>
      {showShare && (
        // It sits under one answer but shares the whole conversation: say so.
        <button
          className="p-0 bg-transparent border-none cursor-pointer"
          onClick={onShare}
          aria-label={t("Share the conversation")}
          title={t("Share the conversation")}
        >
          <IconShare2 {...iconProps} />
        </button>
      )}
      {onPlay != null && (
        <button
          className="p-0 bg-transparent border-none cursor-pointer hidden"
          onClick={onPlay}
        >
          <IconPlayerPlay {...iconProps} />
        </button>
      )}
      {onThumbUp != null && (
        <button
          className="p-0 bg-transparent border-none cursor-pointer hidden"
          onClick={onThumbUp}
        >
          <IconThumbUp {...iconProps} />
        </button>
      )}
      {onThumbDown != null && (
        <button
          className="p-0 bg-transparent border-none cursor-pointer hidden"
          onClick={onThumbDown}
        >
          <IconThumbDown {...iconProps} />
        </button>
      )}
      {onRegenerate != null && (
        <button
          className="p-0 bg-transparent border-none cursor-pointer"
          onClick={onRegenerate}
        >
          <IconRefresh {...iconProps} />
        </button>
      )}
      {onEvaluate != null && (
        <button
          className="p-0 bg-transparent border-none cursor-pointer"
          onClick={onEvaluate}
        >
          <IconClipboardCheck
            stroke={1.5}
            className={
              evaluated === true
                ? "size-5.25 text-ds-mint-800"
                : "size-5.25 text-ds-black"
            }
          />
        </button>
      )}
    </div>
  );
}
